export default class CustomerStoriesTabs {
    constructor(element) {
        this.root = element;

        if (this.root.dataset.customerStoriesReady === '1') {
            return;
        }

        this.nav = this.root.querySelector('[data-customer-stories-nav]');
        this.tabs = Array.from(this.root.querySelectorAll('[role="tab"]'));
        this.heroPanels = Array.from(this.root.querySelectorAll('[data-customer-stories-hero-panel]'));
        this.statsPanels = Array.from(this.root.querySelectorAll('[data-customer-stories-stats-panel]'));
        this.indicator = this.root.querySelector('[data-customer-stories-indicator]');
        this.hoverIndicator = this.root.querySelector('[data-customer-stories-hover-indicator]');
        this.overlayLeft = this.root.querySelector('[data-customer-stories-overlay-left]');
        this.overlayRight = this.root.querySelector('[data-customer-stories-overlay-right]');
        this.bubble = this.root.querySelector('[data-customer-stories-bubble]');
        this.hoveredIndex = -1;
        this.isTouching = false;
        this.resizeTimer = null;

        if (!this.tabs.length) {
            this.root.dataset.customerStoriesReady = '1';
            return;
        }

        this.init();
    }

    init() {
        // Touch detection: suppress hover indicator during swipe
        if (this.nav) {
            this.nav.addEventListener('touchstart', () => { this.isTouching = true; }, { passive: true });
            this.nav.addEventListener('touchend', () => {
                setTimeout(() => { this.isTouching = false; }, 300);
            }, { passive: true });

            this.nav.addEventListener('scroll', () => this.updateOverlays(), { passive: true });
            this.updateOverlays();
        }

        // Hero panel hover → shift bubble right
        const panelsContainer = this.root.querySelector('[data-customer-stories-hero-panels]');
        if (panelsContainer && this.bubble) {
            panelsContainer.addEventListener('mouseenter', () => {
                this.bubble.style.transform = 'translateX(60px)';
            });
            panelsContainer.addEventListener('mouseleave', () => {
                this.bubble.style.transform = '';
            });
        }

        // Tab events
        this.tabs.forEach((tab, index) => {
            tab.addEventListener('mouseenter', () => this.showHoverIndicator(index));
            tab.addEventListener('focus', () => this.showHoverIndicator(index));
            tab.addEventListener('click', () => this.setActiveState(index, false));
            tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
        });

        if (this.nav) {
            this.nav.addEventListener('mouseleave', () => this.hideHoverIndicator());
            this.nav.addEventListener('focusout', (event) => {
                if (!this.nav.contains(event.relatedTarget)) {
                    this.hideHoverIndicator();
                }
            });
        }

        // Hide inactive panels via JS (all panels are visible
        // server-side so content is crawlable without JavaScript).
        this.heroPanels.forEach((panel, i) => {
            if (i !== 0) panel.hidden = true;
        });
        this.statsPanels.forEach((panel, i) => {
            if (i !== 0) panel.hidden = true;
        });

        const initialIndex = this.tabs.findIndex((tab) =>
            tab.getAttribute('aria-selected') === 'true'
        );

        this.setActiveState(initialIndex >= 0 ? initialIndex : 0, false);

        window.addEventListener('resize', () => {
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                const activeIndex = this.tabs.findIndex((tab) =>
                    tab.getAttribute('aria-selected') === 'true'
                );

                this.positionIndicator(this.indicator, activeIndex >= 0 ? activeIndex : 0);
                this.updateOverlays();

                if (this.hoveredIndex >= 0) {
                    this.showHoverIndicator(this.hoveredIndex);
                }
            }, 100);
        });

        this.root.dataset.customerStoriesReady = '1';
    }

    updateOverlays() {
        if (!this.nav) return;

        const sl = this.nav.scrollLeft;
        const maxScroll = this.nav.scrollWidth - this.nav.clientWidth;

        if (maxScroll <= 0) {
            if (this.overlayLeft) this.overlayLeft.style.opacity = '0';
            if (this.overlayRight) this.overlayRight.style.opacity = '0';
            return;
        }

        if (this.overlayLeft) this.overlayLeft.style.opacity = sl > 4 ? '1' : '0';
        if (this.overlayRight) this.overlayRight.style.opacity = sl < maxScroll - 4 ? '1' : '0';
    }

    positionIndicator(targetIndicator, index) {
        if (!targetIndicator || !this.tabs[index]) {
            return;
        }

        targetIndicator.style.width = this.tabs[index].offsetWidth + 'px';
        targetIndicator.style.transform = 'translateX(' + this.tabs[index].offsetLeft + 'px)';
    }

    showHoverIndicator(index) {
        if (this.isTouching || !this.hoverIndicator || !this.tabs[index]) {
            return;
        }

        this.hoveredIndex = index;
        this.positionIndicator(this.hoverIndicator, index);
        this.hoverIndicator.style.opacity = '1';
    }

    hideHoverIndicator() {
        this.hoveredIndex = -1;

        if (this.hoverIndicator) {
            this.hoverIndicator.style.opacity = '0';
        }
    }

    centerTab(index) {
        if (!this.nav || !this.tabs[index]) return;
        const tab = this.tabs[index];
        const scrollTarget = tab.offsetLeft - (this.nav.clientWidth / 2) + (tab.offsetWidth / 2);
        this.nav.scrollTo({ left: scrollTarget, behavior: 'smooth' });
    }

    setActiveState(index, focusTab) {
        this.tabs.forEach((tab, tabIndex) => {
            const isActive = tabIndex === index;

            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.setAttribute('tabindex', isActive ? '0' : '-1');

            if (focusTab && isActive) {
                tab.focus();
            }
        });

        this.heroPanels.forEach((panel, panelIndex) => {
            panel.hidden = panelIndex !== index;
        });

        this.statsPanels.forEach((panel, panelIndex) => {
            panel.hidden = panelIndex !== index;
        });

        this.positionIndicator(this.indicator, index);
        this.centerTab(index);
    }

    handleKeydown(event, index) {
        let nextIndex = index;

        if (event.key === 'ArrowRight') {
            nextIndex = (index + 1) % this.tabs.length;
        } else if (event.key === 'ArrowLeft') {
            nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        } else if (event.key === 'Home') {
            nextIndex = 0;
        } else if (event.key === 'End') {
            nextIndex = this.tabs.length - 1;
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setActiveState(index, false);
            return;
        } else {
            return;
        }

        event.preventDefault();
        this.setActiveState(nextIndex, true);
    }
}
